import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import Input from '../../../components/Form/Input';
import Select from '../../../components/Form/Select';
import { OutTransactionFormData, DropdownOption } from './outTransactionTypes';

interface OutBasicInfoSectionProps {
  register: UseFormRegister<OutTransactionFormData>;
  errors: FieldErrors<OutTransactionFormData>;
  currencyOptions: DropdownOption[];
  selectedCurrencyCode: string;
  /** Currency code of the linked bill, e.g. "USD" (empty when no bill) */
  billCurrencyCode?: string;
  amountValue?: number;
  exchangeRateValue?: number;
}

const OutBasicInfoSection: React.FC<OutBasicInfoSectionProps> = ({
  register,
  errors,
  currencyOptions,
  selectedCurrencyCode,
  billCurrencyCode = '',
  amountValue = 0,
  exchangeRateValue = 0,
}) => {
  // Rate always shows for non-EGP payments; it also shows when paying EGP
  // against a foreign-currency bill (EGP ÷ rate = bill currency)
  const egpAgainstForeignBill =
    selectedCurrencyCode === 'EGP' && !!billCurrencyCode && billCurrencyCode !== 'EGP';
  const showRate = selectedCurrencyCode !== 'EGP' || egpAgainstForeignBill;
  const rateCurrency = selectedCurrencyCode !== 'EGP' ? selectedCurrencyCode : billCurrencyCode;

  const conversionApplies =
    !!billCurrencyCode && !!selectedCurrencyCode &&
    billCurrencyCode !== selectedCurrencyCode &&
    (amountValue || 0) > 0 && (exchangeRateValue || 0) > 0;
  const appliedToBill = !conversionApplies ? 0
    : selectedCurrencyCode === 'EGP' ? (amountValue / exchangeRateValue)
    : billCurrencyCode === 'EGP' ? (amountValue * exchangeRateValue)
    : amountValue;

  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        Basic Information
      </h3>
      <div className="space-y-6">
        <Input
          label="Description"
          type="text"
          required
          {...register('description', { required: 'Description is required' })}
          error={errors.description?.message}
          placeholder="Enter transaction description"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Currency"
            {...register('currency', { required: 'Currency is required' })}
            options={currencyOptions}
            error={errors.currency?.message}
          />
          <div></div>
        </div>

        {showRate && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={`Exchange Rate (${rateCurrency} to EGP)`}
              type="number"
              step="0.01"
              required
              {...register('exchangeRate', {
                required: 'Exchange rate is required',
                min: { value: 0.01, message: 'Exchange rate must be greater than 0' }
              })}
              error={errors.exchangeRate?.message}
              placeholder="e.g., 50.00"
              helperText={egpAgainstForeignBill
                ? `EGP amount ÷ rate = ${billCurrencyCode} applied to the bill`
                : undefined}
            />
            <div></div>
          </div>
        )}

        {conversionApplies && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Applied to the bill:{' '}
              <strong>
                {appliedToBill.toLocaleString(undefined, { maximumFractionDigits: 2 })} {billCurrencyCode}
              </strong>
              {' '}
              ({selectedCurrencyCode === 'EGP'
                ? `${Number(amountValue).toLocaleString()} EGP ÷ ${exchangeRateValue}`
                : `${Number(amountValue).toLocaleString()} ${selectedCurrencyCode} × ${exchangeRateValue}`})
            </p>
          </div>
        )}

        {selectedCurrencyCode !== 'EGP' && !conversionApplies && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> If you enter both amount and exchange rate, the EGP equivalent will be calculated automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutBasicInfoSection;
